<template>
  <div
    :class="[$style.board_container, 'board_container']"
  >
    <template v-if="showFilterStub">
      <div
        :class="$style.no_tasks_stub"
      >
        <svgImage
          :class="$style.stub_svg"
          name="comment-hub-no-search"
        />
        <div
          :class="$style.no_tasks_text"
        >
          {{ locale('board_no_filtered_tasks') }}
        </div>
      </div>
    </template>
    <template
      v-if="showSkeleton"
    >
      <board-skeleton />
    </template>
    <template
      v-if="showColumns"
    >
      <draggable
        v-model="draggableList"
        :class="[
          $style.columns_container,
          'columns_container',
          'gantt_scroll',
          'thumb-spaced',
          columnDragging || taskDragging ? $style.drag_active : '',
        ]"
        :group="{ name: 'columns' }"
        :item-key="column => `${column.id}_${column.value}`"
        v-bind="{
          disabled: sharedView || taskCardEditing || taskSettingsInfo,
          group: 'columns',
          animation: 200,
          filter: '.add_field_option',
          ghostClass: $style.drop_preview,
          handle: '.column_drag_handle',
          forceFallback: true,
          fallbackClass: $style.column_drag,
          fallbackTolerance: 5,
          chosenClass: $style.column_drag_chosen,
          easing: 'cubic-bezier(1, 0, 0, 1)',
          scrollSensitivity: 300,
          scrollSpeed: 25
        }"
        :move="onColumnMove"
        @start="onColumnDragStart"
        @end="onColumnDrop"
        @scroll="onBoardContainerScroll"
      >
        <template #item="{element: column, index}">
          <div
            v-if="column.isOptionColumn"
            :class="[$style.add_option, 'add_field_option']"
          >
            <iconButton
              :class="$style.add_option__button"
              :icon="{ name: 'plus-1', type: 'regular', size: 24 }"
              @onClick="editField"
            />
          </div>
          <column
            v-else
            :key="`${column.id}_${column.value}`"
            :ref="setColumnRef"
            :style="{ width: `${colWidth}px` }"
            :is-collapsed="!!collapsed[column.id]"
            :tasks-map="tasksMap"
            :project-resources="projectResources"
            :column="column"
            :order="[index, columns.length]"
            :task-dragging="taskDragging"
            :accept-drag-item="!dragPutRestricted.includes(column.id)"
            :can-drag-tasks="canDragTasks"
            :board-height="boardHeight"
            :draft-tasks="draftTasks"
            @onTaskDrop="onTaskDrop"
            @onCollapse="onCollapse"
            @taskChange="taskChange"
            @createTask="createTask"
            @taskDragStart="taskDragStart"
          />
        </template>
      </draggable>
    </template>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import { mapGetters } from 'vuex';

import Column from './Column.vue';
import iconButton from '$$uiKitComp/globalButton/withIcon/iconButton.vue';
import svgImage from '$$uiKitComp/VueIcon/vgpSvgImage.vue';

import rights from '../../../components/rights';
import BoardSkeleton from './BoardSkeleton.vue';

export default {
  name: 'BoardView',
  components: {
    BoardSkeleton,
    draggable,
    Column,
    iconButton,
    svgImage,
  },
  provide() {
    return {
      getAllColumnsRefs: () => this.allColumnsRefs,
    };
  },
  inject: [
    'closeContextMenus',
    'hideResourcePopup',
  ],
  props: {
    columns: {
      type: Array,
      default: [],
    },
    colWidth: {
      type: Number,
      default: 324,
    },
    showFilterStub: {
      type: Boolean,
      default: false,
    },
    columnDragging: {
      type: Boolean,
      default: false,
    },
    taskDragging: {
      type: Boolean,
      default: false,
    },
    collapsed: {
      type: Object,
      default: {},
    },
    dragPutRestricted: {
      type: Array,
      default: [],
    },
    canDragTasks: {
      type: Boolean,
      default: false,
    },
    tasksMap: {
      type: Object,
      default: {},
    },
    projectResources: {
      type: Object,
      default: {},
    },
    sharedView: {
      type: Boolean,
      default: false,
    },
    boardHeight: {
      type: Number,
      default: 1500,
    },
    draftTasks: {
      type: Array,
      default: [],
    },
  },
  emits: [
    'onColumnDragStart',
    'onColumnDrop',
    'onTaskDrop',
    'onCollapse',
    'taskChange',
    'createTask',
    'taskDragStart',
    'editField',
  ],
  data() {
    return {
      locale: __,
    };
  },
  computed: {
    draggableList() {
      if (!this.sharedView && this.groupingFieldInfo.isCustom && this.canEditCustomFields) {
        return [
          ...this.columns,
          { isOptionColumn: true },
        ];
      }

      return this.columns;
    },
    canEditCustomFields() {
      return rights.account.hasRight('custom_fields_edit');
    },
    showSkeleton() {
      return !this.columns.length;
    },
    showColumns() {
      return !(this.showFilterStub || this.showSkeleton);
    },
    ...mapGetters({
      groupingFieldInfo: 'board/groupingFieldInfo',
      taskCardEditing: 'board/taskCardEditing',
      taskSettingsInfo: 'board/taskSettingsInfo',
      projectId: 'board/projectId',
    }),
  },
  beforeUpdate() {
    this.allColumnsRefs = [];
  },
  methods: {
    setColumnRef(value) {
      if (value) {
        this.allColumnsRefs.push(value);
      }
    },
    onColumnDragStart(e) {
      this.closeContextMenus();

      const fallbackNode = Array.from(e.from.children).find(child => child.classList.contains('sortable-drag'));
      const columnVue = this.allColumnsRefs.find(columnRef => columnRef.$el === e.item);

      if (fallbackNode && columnVue) {
        fallbackNode.children[1].scrollTop = columnVue.scrollTop;
      }

      this.$emit('onColumnDragStart');
    },
    onColumnDrop(e) {
      this.$emit('onColumnDrop', {
        addedIndex: e.newIndex,
        removedIndex: e.oldIndex,
        payload: this.columns[e.oldIndex],
      });

      this.$nextTick(() => {
        this.allColumnsRefs.forEach(column => {
          const scrollTop = column.scrollTop;

          if (scrollTop) {
            column.scrollColumnTo({ y: scrollTop });
          }
        });
      });
    },
    onColumnMove(e) {
      return !e.related.classList.contains('add_field_option');
    },
    onTaskDrop(e) {
      this.$emit('onTaskDrop', e);
    },
    onCollapse(e) {
      this.$emit('onCollapse', e);
    },
    taskChange(e) {
      this.$emit('taskChange', e);
    },
    createTask(name, columnId, columnComponent) {
      this.$emit('createTask', name, columnId, columnComponent);
    },
    taskDragStart(e) {
      this.$emit('taskDragStart', e);
    },
    editField() {
      this.$emit('editField');
    },
    onBoardContainerScroll() {
      this.closeContextMenus();
      this.hideResourcePopup();
    },
  },
};
</script>

<style module src="../less/board.less"></style>
